import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Box,
  Container,
  ThemeProvider,
  styled,
} from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { ContentContainer } from './ActivationSteps.styles';

import { ORGANIZATIONS_BY_PREFIX_QUERY } from '@/apollo/queries';
import Button from '@/components/Button';
import { TextField } from '@/components';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { getTheme, type Product } from '@/utils/productHelpers';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { logError } from '@/utils/logError';

const DROPDOWN_RESULTS_LIMIT = 3;
const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const SLEEPIO_CANOPY_LOB = 'wellness';

interface Organization {
  name?: string | null;
  slug: string;
}

const SuggestionsContainer = styled(Paper)({
  position: 'absolute',
  zIndex: 1000,
  width: '100%',
  maxHeight: 200,
  overflow: 'auto',
  marginTop: 4,
});

const InputContainer = styled(Box)({
  position: 'relative',
  marginBottom: 32,
});

interface SuggestionContentProps {
  loading: boolean;
  error: boolean;
  organizations: Organization[];
  onSelect: (org: Organization) => void;
}

const SuggestionContent: React.FC<SuggestionContentProps> = ({
  loading, error, organizations, onSelect,
}) => {
  if (error) {
    return (
      <ListItem>
        <ListItemText primary="Something went wrong. Clear the field and try again." />
      </ListItem>
    );
  }
  if (loading) {
    return (
      <ListItem>
        <ListItemText primary="Searching…" />
      </ListItem>
    );
  }
  if (organizations.length > 0) {
    return (
      <List>
        {organizations.map((org) => (
          <ListItem
            key={org.slug}
            button
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(org);
            }}
          >
            <ListItemText primary={org.name ?? org.slug} />
          </ListItem>
        ))}
      </List>
    );
  }
  return (
    <ListItem>
      <ListItemText primary="No employers found. Try a different search term." />
    </ListItem>
  );
};

const EmployerSelection: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  // Route is a static path — use useRouteMatch to extract the product segment.
  const match = useRouteMatch<{ product: string }>('/activate/:product/wellness');
  const product = (match?.params.product ?? 'sleepio-canopy') as Product;

  const lastRequestId = useRef(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [queryError, setQueryError] = useState(false);

  const [searchOrganizations, { loading: searchLoading }] = useLazyQuery(
    ORGANIZATIONS_BY_PREFIX_QUERY,
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (selectedOrg || searchQuery.length < MIN_QUERY_LENGTH) {
        setOrganizations([]);
        setShowSuggestions(false);
        return;
      }

      lastRequestId.current += 1;
      const requestId = lastRequestId.current;

      searchOrganizations({
        variables: {
          prefix: searchQuery,
          limit: DROPDOWN_RESULTS_LIMIT,
          searchFilters: JSON.stringify({ organization_type: 'emp' }),
        },
        onCompleted: (data) => {
          if (requestId !== lastRequestId.current) { return; }
          setOrganizations(data?.organizationsByPrefix || []);
          setShowSuggestions(true);
          setQueryError(false);
        },
        onError: (err) => {
          if (requestId !== lastRequestId.current) { return; }
          logError('[EmployerSelection]', 'organizationsByPrefix query failed', err);
          setQueryError(true);
          setOrganizations([]);
          setShowSuggestions(true);
        },
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedOrg, searchOrganizations]);

  const handleInputChange = (value: string) => {
    setSearchQuery(value);
    setQueryError(false);
    if (selectedOrg && value !== (selectedOrg.name ?? selectedOrg.slug)) {
      setSelectedOrg(null);
    }
  };

  const handleOrganizationSelect = (org: Organization) => {
    setSelectedOrg(org);
    setSearchQuery(org.name ?? org.slug);
    setShowSuggestions(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) { return; }

    const params = new URLSearchParams(location.search);
    const redirectTo = params.get('redirect_to');
    const redirectParam = redirectTo && isSafeRedirectUri(redirectTo)
      ? `?redirect_to=${encodeURIComponent(redirectTo)}`
      : '';

    history.push(
      `/activate/${product}/${SLEEPIO_CANOPY_LOB}/${selectedOrg.slug}/sign-up${redirectParam}`,
    );
  };

  return (
    <ThemeProvider theme={getTheme(product)}>
      <ContentContainer>
        <SleepioHeader />
        <Container maxWidth="xs" style={{ flex: 1 }}>
          <Typography variant="h2" style={{ marginTop: 32 }} color="secondary">
            Find your employer
          </Typography>
          <Typography style={{ marginTop: 8, marginBottom: 32 }} color="secondary">
            Search for your employer to get started.
          </Typography>
          <form onSubmit={handleSubmit}>
            <InputContainer>
              <TextField
                id="employer-name-input"
                autoFocus
                required
                label="Employer name"
                value={searchQuery}
                onChange={(e) => handleInputChange(e.target.value)}
                inputProps={{ placeholder: 'Start typing your employer…', 'aria-label': 'Employer name' }}
              />
              {showSuggestions && (
                <SuggestionsContainer elevation={3}>
                  <SuggestionContent
                    loading={searchLoading}
                    error={queryError}
                    organizations={organizations}
                    onSelect={handleOrganizationSelect}
                  />
                </SuggestionsContainer>
              )}
            </InputContainer>
            <Button
              variant="contained"
              type="submit"
              fullWidth
              color="primary"
              disabled={!selectedOrg}
              rounded
            >
              Continue
            </Button>
          </form>
        </Container>
        <Footer />
      </ContentContainer>
    </ThemeProvider>
  );
};

export default EmployerSelection;
